import React from "react";

import Big, { BigSource } from "big.js";

export interface NumberProps {
  value: BigSource;
  suffix?: string;
  prefix?: string;
  // Optional number to multiply by to convert between units
  multiply?: BigSource;
  // Formatting options
  minDecimals?: number;
  maxDecimals?: number;
  significantDigits?: number;
}

const Number: React.FC<NumberProps> = ({
  value,
  suffix = "",
  prefix = "",
  multiply,
  minDecimals = 2,
  maxDecimals,
  significantDigits,
}) => {
  try {
    let big = Big(value);
    if (multiply) {
      big = big.mul(multiply);
    }

    //if the number is <1 and we have significant digits, use toPrecision
    const shouldUseSig = Big(1).gt(big) && !!significantDigits;

    if (maxDecimals !== void 0 && !shouldUseSig) {
      big = big.round(maxDecimals);
    }

    // eslint-disable-next-line prefer-const
    let [integer, fraction = ""] = (
      shouldUseSig ? big.toPrecision(significantDigits, 0) : big.toString()
    ).split(".");

    if (
      multiply === 0.001 &&
      integer &&
      parseInt(integer, 10) > 0 &&
      fraction[0] &&
      parseInt(fraction[0], 10) >= 5
    ) {
      integer = (parseInt(integer, 10) + 1).toString();
    }

    const letters = (integer ?? "").split("").reverse();

    let withCommas = "";
    for (let i = 0; i < letters.length; i++) {
      withCommas = `${letters[i]}${withCommas}`;

      if (i % 3 === 2 && letters[i + 1]) {
        withCommas = `,${withCommas}`;
      }
    }

    let decimals = fraction.padEnd(minDecimals, "0");
    if (!significantDigits) {
      decimals = decimals.substring(0, maxDecimals);
    }

    let isGreaterThanOne = false;
    if (multiply && big.round().toNumber() > 1) {
      isGreaterThanOne = true;
    }

    return isGreaterThanOne ? (
      <>{`${prefix}${withCommas}${suffix}`}</>
    ) : (
      <>{`${prefix}${withCommas}${decimals ? `.${decimals}` : ""}${suffix}`}</>
    );
  } catch (e) {
    // Big.js could not parse
    return <>{value}</>;
  }
};

export default Number;
